import React, { useState, useEffect } from 'react';
import { MessageSquarePlus, Star, X } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useLocation } from 'react-router-dom';

const FeedbackButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      // Get the current page path and clean it up
      const currentPage = location.pathname;
      const pageName = currentPage === '/' ? 'home' : currentPage.replace(/^\/+|\/+$/g, '');

      const { error } = await supabase
        .from('user_feedback')
        .insert({
          user_id: user.id,
          feedback,
          rating,
          page: pageName, // Store the page information
          is_premium: user.isPremium, // Store premium status
          user_email: user.email, // Store user email for easier identification
          user_name: user.name // Store user name
        });

      if (error) throw error;

      showToast('Thank you for your feedback!', 'success');
      setIsOpen(false);
      setFeedback('');
      setRating(0);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showToast('Failed to submit feedback. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-[#7B4AE2] text-white p-3 rounded-full shadow-lg hover:bg-[#6A3ACF] transition-colors z-50"
        aria-label="Give Feedback"
      >
        <MessageSquarePlus className="h-6 w-6" />
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Share Your Feedback</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How would you rate your experience?
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRating(value)}
                      className="p-2"
                    >
                      <Star
                        className={`h-6 w-6 ${
                          value <= rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your feedback
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#7B4AE2] focus:ring focus:ring-[#7B4AE2] focus:ring-opacity-50"
                  rows={4}
                  placeholder="Tell us what you think..."
                  required
                ></textarea>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!rating || !feedback || isSubmitting}
                  className="px-4 py-2 bg-[#7B4AE2] text-white rounded-md hover:bg-[#6A3ACF] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;